
import React, { ButtonHTMLAttributes } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'accent';
  isLoading?: boolean;
  icon?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({ 
  children, 
  variant = 'primary', 
  isLoading = false, 
  icon,
  className = '',
  disabled,
  ...props 
}) => {
  const baseStyles = "inline-flex items-center justify-center px-4 py-2 border text-sm font-semibold rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:focus:ring-offset-slate-900";
  
  const variants = {
    primary: "border-transparent text-white bg-primary hover:bg-slate-800 focus:ring-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 dark:focus:ring-slate-500 shadow-md hover:shadow-lg",
    secondary: "border-gray-200 text-gray-700 bg-white hover:bg-gray-50 focus:ring-primary shadow-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700",
    // Accent using new Teal palette
    accent: "border-transparent text-white bg-accent hover:bg-accent-hover focus:ring-accent dark:bg-accent-dark dark:hover:bg-accent-darkHover dark:text-slate-900 shadow-md hover:shadow-lg",
    danger: "border-red-200 text-red-700 bg-red-50 hover:bg-red-100 focus:ring-red-500 dark:bg-red-900/20 dark:border-red-900/30 dark:text-red-400 dark:hover:bg-red-900/30",
    ghost: "border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-100 shadow-none dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800"
  };

  return (
    <button 
      className={`${baseStyles} ${variants[variant]} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {!isLoading && icon && <span className="mr-2">{icon}</span>}
      {children}
    </button>
  );
};
